/**
 * WeightLossIntake Page - Nimbus Style
 * 
 * GLP-1 Weight Loss Intake Form - 8-step flow with MDI Readiness
 * Production-ready with demographics persistence and MDI integration.
 * Styled to match nimbus-products-ui WeightWise design.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  ProgressBar,
  MDIReadinessStep,
  GoalsStep,
  CurrentMedicationsStep,
  MedicalScreeningStep,
  SafetyAssessmentStep,
  LifestyleStep,
  ReferralContextStep,
  ReviewStep,
} from '@/components/intake/weightloss'
import ShippingBillingAddressStep from '@/components/intake/ShippingBillingAddressStep'
import { getIntakePrefillData } from '@/services/intakeService'
import { mapPrefillToWeightLossIntake } from '@/lib/intake/prefillMapper'

// Import Nimbus styling
import '@/styles/nimbus-intake.css'

// Phone validation helper - must be 10-11 digits
const isValidPhone = (phone: string): boolean => {
  if (!phone?.trim()) return false
  const digitsOnly = phone.replace(/\D/g, '')
  if (digitsOnly.length < 10 || digitsOnly.length > 11) return false
  // Check for obvious invalid patterns (all same digit)
  if (/^(\d)\1+$/.test(digitsOnly)) return false
  return true
}

const STEP_TITLES = [
  'Verify Info',
  'Goals',
  'Medications',
  'Medical History',
  'Safety',
  'Lifestyle',
  'Referral',
  'Address',
  'Review',
]

interface WeightLossFormData {
  // Step 1: MDI Readiness
  firstName: string
  lastName: string
  dateOfBirth: string
  gender: string
  email: string
  phone: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  telehealthLocationState: string
  governmentIdKey: string
  demographicsConfirmed: boolean
  demographicsUpdated: boolean
  smsConsent: boolean

  // Step 2: Goals
  currentWeight: number | null
  goalWeight: number | null
  heightFeet: number | null
  heightInches: number | null
  weightLossGoal: string
  weightDistribution: string
  previousAttempts: string

  // Step 3: Current GLP-1 Medications
  currentGlp1Type: string
  glp1Details: {
    drugName: string
    dose: string
    concentration?: string
    unitsPerWeek?: string
    frequency?: string
    lastDoseDate: string
    dosePreference: string
    prescriptionPhotoKey?: string
  }

  // Step 4: Medical Screening
  metabolicConditions: string[]
  contraindications: string[]
  contraindicationDetails: Record<string, string>
  familyHistory: string[]
  kidneyConditions: string[]
  kidneyDetails: Record<string, string>

  // Step 5: Safety Assessment
  glp1Consent: boolean
  pregnancyStatus: string
  lastProviderVisit: string
  hasRecentLabs: boolean
  labResultsPhotoKey?: string

  // Step 6: Lifestyle
  willingReduceCalories: boolean
  willingExercise: boolean
  bloodPressureSystolic: string
  bloodPressureDiastolic: string
  bloodPressureUnknown: boolean
  allergies: string
  currentMedications: string
  wantsNauseaMedication: boolean
  additionalNotes: string

  // Step 7: Referral Context
  referringClinicName: string
  referralReason: string
  priorFailedTherapies: string
  recentLabsPhotoKey?: string
  specialConsiderations: string

  // Step 8: Shipping & Billing Address
  shippingAddressLine1: string
  shippingAddressLine2: string
  shippingCity: string
  shippingState: string
  shippingPostalCode: string
  billingAddressLine1: string
  billingAddressLine2: string
  billingCity: string
  billingState: string
  billingPostalCode: string
  billingSameAsShipping: boolean

  // Step 9: Consent
  consentShareWithMDI: boolean
  consentAsyncWorkflow: boolean
  consentTelemedicine: boolean
  consentAccurate: boolean
  consentTerms: boolean
}

const initialFormData: WeightLossFormData = {
  // Step 1: MDI Readiness
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  telehealthLocationState: '',
  governmentIdKey: '',
  demographicsConfirmed: false,
  demographicsUpdated: false,
  smsConsent: true,

  // Step 2: Goals
  currentWeight: null,
  goalWeight: null,
  heightFeet: null,
  heightInches: null,
  weightLossGoal: '',
  weightDistribution: '',
  previousAttempts: '',

  // Step 3: Current GLP-1 Medications
  currentGlp1Type: '',
  glp1Details: {
    drugName: '',
    dose: '',
    concentration: '',
    unitsPerWeek: '',
    frequency: '',
    lastDoseDate: '',
    dosePreference: '',
    prescriptionPhotoKey: '',
  },

  // Step 4: Medical Screening
  metabolicConditions: [],
  contraindications: [],
  contraindicationDetails: {},
  familyHistory: [],
  kidneyConditions: [],
  kidneyDetails: {},

  // Step 5: Safety Assessment
  glp1Consent: false,
  pregnancyStatus: '',
  lastProviderVisit: '',
  hasRecentLabs: false,
  labResultsPhotoKey: '',

  // Step 6: Lifestyle
  willingReduceCalories: false,
  willingExercise: false,
  bloodPressureSystolic: '',
  bloodPressureDiastolic: '',
  bloodPressureUnknown: false,
  allergies: '',
  currentMedications: '',
  wantsNauseaMedication: false,
  additionalNotes: '',

  // Step 7: Referral Context
  referringClinicName: '',
  referralReason: '',
  priorFailedTherapies: '',
  recentLabsPhotoKey: '',
  specialConsiderations: '',

  // Step 8: Shipping & Billing Address
  shippingAddressLine1: '',
  shippingAddressLine2: '',
  shippingCity: '',
  shippingState: '',
  shippingPostalCode: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingCity: '',
  billingState: '',
  billingPostalCode: '',
  billingSameAsShipping: true,

  // Step 9: Consent
  consentShareWithMDI: false,
  consentAsyncWorkflow: false,
  consentTelemedicine: false,
  consentAccurate: false,
  consentTerms: false,
}

// Landing page for Weight Loss Intake
export const WeightLossIntakeLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [isValidating, setIsValidating] = useState(true)
  const [isValid, setIsValid] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [patientInfo, setPatientInfo] = useState<{ firstName?: string; lastName?: string } | null>(null)
  const [clinicName, setClinicName] = useState<string>('')
  const [landingTheme, setLandingTheme] = useState<'dark' | 'light'>('dark')

  useEffect(() => {
    const validateToken = async () => {
      // Demo token support
      if (token === 'demo-weightloss' || token === 'demo-glp1') {
        setIsValid(true)
        setPatientInfo({ firstName: 'Demo', lastName: 'Patient' })
        setClinicName('Demo Clinic')
        setIsValidating(false)
        return
      }

      try {
        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
        const response = await fetch(`${apiUrl}/intakes/validate/${token}`)
        const data = await response.json()

        if (data.success) {
          // Check therapy category - should be glp1
          const therapyCategory = data.data?.therapyCategory || data.therapyCategory
          if (therapyCategory && therapyCategory !== 'glp1') {
            // Redirect to correct form
            if (therapyCategory === 'hrt') {
              navigate(`/patient/intake/womens/${token}`, { replace: true })
              return
            } else if (therapyCategory === 'trt') {
              navigate(`/patient/intake/mens/${token}`, { replace: true })
              return
            } else {
              navigate(`/patient/intake/${token}`, { replace: true })
              return
            }
          }

          setIsValid(true)
          const patientData = data.intake?.patientInfo || data.patient || data.data?.patient
          if (patientData) {
            setPatientInfo({
              firstName: patientData.firstName || patientData.first_name,
              lastName: patientData.lastName || patientData.last_name,
            })
          }
          // Get clinic name for referral context
          const resolvedClinicName = data.data?.clinicName || data.data?.clinic?.name || data.clinic?.name
          if (resolvedClinicName) {
            setClinicName(resolvedClinicName)
          }
        } else {
          setIsValid(false)
          setValidationError(data.message || 'This intake link is invalid or has expired.')
        }
      } catch (error) {
        console.error('Token validation error:', error)
        setIsValid(false)
        setValidationError('Unable to validate intake link. Please try again.')
      } finally {
        setIsValidating(false)
      }
    }

    validateToken()
  }, [token, navigate])

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1816]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-[#A8A29E]">Validating your intake request...</p>
        </div>
      </div>
    )
  }

  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1816]">
        <div className="max-w-md text-center p-8 bg-[#22201E] rounded-2xl shadow-lg border border-[rgba(245,240,234,0.08)]">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-destructive/10">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-[#F5F0EA] font-montserrat">
            Invalid or Expired Link
          </h1>
          <p className="mb-6 text-[#A8A29E]">
            {validationError || 'This intake form link is invalid or has expired.'}
          </p>
          <p className="text-sm text-[#6B6560]">
            Please contact your healthcare provider for a new link.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="relative min-h-dvh flex flex-col md:flex-row transition-colors duration-500"
      style={{ backgroundColor: landingTheme === 'dark' ? '#1A1816' : '#F5F0EA' }}
    >
      {/* Left: Hero Image — 60% on desktop, fullscreen background on mobile */}
      <div className="absolute inset-0 md:relative md:w-[60%] md:min-h-dvh overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1400&q=80"
          alt="Wellness journey"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/75 md:bg-black/10" />
      </div>

      {/* Right: Content Panel — 40% on desktop */}
      <div
        className="relative z-10 flex-1 md:w-[40%] flex flex-col min-h-dvh bg-transparent transition-colors duration-500"
        style={{ backgroundColor: landingTheme === 'dark' ? undefined : '#F5F0EA' }}
      >
        {/* Desktop bg for dark mode only */}
        {landingTheme === 'dark' && (
          <div className="hidden md:block absolute inset-0 bg-[#1A1816]" />
        )}
        {/* Flat Nimbus background — no ambient glow */}

        {/* Top bar: Logo + clinic name + theme toggle */}
        <div className="relative z-10 flex items-center justify-between px-8 pt-8 md:pt-10">
          <div className="flex items-center gap-3">
            <img
              src="/img/nim-logo-black.svg"
              alt="Nimbus Healthcare"
              width={120}
              height={34}
              className={landingTheme === 'dark' ? 'brightness-0 invert' : ''}
            />
            {clinicName && (
              <>
                <span className={`w-px h-4 ${landingTheme === 'dark' ? 'bg-white/15' : 'bg-black/15'}`} />
                <span className={`text-[11px] font-medium uppercase tracking-wider ${landingTheme === 'dark' ? 'text-white/50' : 'text-black/40'}`}>
                  {clinicName}
                </span>
              </>
            )}
          </div>
          <button
            className={`hidden md:flex items-center justify-center w-9 h-9 rounded-full border cursor-pointer transition-all duration-200 ${
              landingTheme === 'dark'
                ? 'bg-white/[0.05] border-white/10 text-white/60 hover:bg-white/[0.08] hover:text-white hover:border-white/20'
                : 'bg-black/[0.04] border-black/10 text-black/50 hover:bg-black/[0.08] hover:text-black hover:border-black/20'
            }`}
            onClick={() => setLandingTheme(landingTheme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
          >
            {landingTheme === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>

        {/* Main content — vertically centered */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 md:px-10 text-center">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md mb-8 border ${
            landingTheme === 'dark'
              ? 'bg-black/30 md:bg-white/[0.04] border-white/15 md:border-white/10'
              : 'bg-black/5 border-black/10'
          }`}>
            <span className="w-2 h-2 rounded-full bg-[#4A8A9A] animate-pulse" />
            <span className={`text-[13px] font-medium tracking-wide ${
              landingTheme === 'dark' ? 'text-white/90 md:text-white/70' : 'text-black/60'
            }`}>GLP-1 Weight Management</span>
          </div>

          {/* Heading */}
          <h1
            className={`text-[clamp(36px,6vw,56px)] leading-[1.1] m-0 mb-6 transition-colors duration-500 font-normal ${
              landingTheme === 'dark' ? 'text-[#F5F0EA]' : 'text-[#1A1816]'
            }`}
            style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-1px' }}
          >
            Made by Us,{' '}
            <br className="hidden sm:block" />
            for You<span className="text-[#B87A5D]">.</span>
          </h1>

          {/* Subtitle */}
          <p className={`text-[clamp(15px,2vw,17px)] leading-relaxed max-w-[380px] m-0 mb-3 transition-colors duration-500 ${
            landingTheme === 'dark' ? 'text-white/70' : 'text-black/55'
          }`}>
            One flat rate unlocks your provider, support, and personalized dosing.
          </p>

          {/* Secondary text */}
          <p className={`text-[clamp(13px,1.8vw,15px)] leading-relaxed max-w-[340px] m-0 mb-10 transition-colors duration-500 ${
            landingTheme === 'dark' ? 'text-white/45' : 'text-black/35'
          }`}>
            Designed to adapt as your body and goals evolve.
          </p>

          {/* CTA Button */}
          <button
            onClick={() => navigate(`/patient/intake/weightloss/${token}/form`)}
            className="group inline-flex items-center gap-2.5 py-4 px-10 text-[16px] font-semibold bg-[#B87A5D] text-[#F5F0EA] border-none rounded-full cursor-pointer transition-all duration-350 shadow-[0_4px_20px_rgba(184,122,93,0.25)] hover:-translate-y-0.5 hover:bg-[#A56A4D] hover:shadow-[0_8px_32px_rgba(184,122,93,0.35)] active:translate-y-0"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Find Your Program
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5F0EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover:translate-x-1">
              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
            </svg>
          </button>

          {/* Helper text */}
          <p className={`text-[13px] mt-5 transition-colors duration-500 ${
            landingTheme === 'dark' ? 'text-white/40' : 'text-black/35'
          }`}>
            Takes about 4 minutes
          </p>
        </div>

        {/* Bottom trust indicators */}
        <div className="relative z-10 flex items-center justify-center gap-4 px-8 pb-8">
          <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.06em] ${
            landingTheme === 'dark' ? 'text-white/40' : 'text-black/35'
          }`}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>HIPAA Compliant</span>
          </div>
          <span className={`w-px h-3 ${landingTheme === 'dark' ? 'bg-white/20' : 'bg-black/15'}`} />
          <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.06em] ${
            landingTheme === 'dark' ? 'text-white/40' : 'text-black/35'
          }`}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>256-bit Encrypted</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Main Form Component
export const WeightLossIntakeForm: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<WeightLossFormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const [isValidating, setIsValidating] = useState(true)
  const [isCancelled, setIsCancelled] = useState(false)
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null)
  const [prefillData, setPrefillData] = useState<any>(null)
  const [clinicName, setClinicName] = useState<string>('')
  const [showReferralStep, setShowReferralStep] = useState(true) // Default to true for MDI clinics
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const totalSteps = showReferralStep ? 9 : 8
  const containerRef = useRef<HTMLDivElement>(null)

  // Scroll to top on step change with smooth behavior
  useEffect(() => {
    if (!isValidating && !isCancelled && !isSubmitted) {
      setTimeout(() => {
        try {
          if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        } catch {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }, 20)
    }
  }, [currentStep, isValidating, isCancelled, isSubmitted])

  // Validate token on mount and load initial data
  useEffect(() => {
    const validateAndLoadData = async () => {
      if (!token || token.startsWith('demo')) {
        // Demo mode - set some prefill data
        if (token?.startsWith('demo')) {
          const demoPrefill = {
            firstName: 'Demo',
            lastName: 'Patient',
            email: 'demo@example.com',
            phone: '5551234567',
            dateOfBirth: '1985-06-15',
            gender: 'female',
          }
          setPrefillData(demoPrefill)
          setFormData(prev => ({ ...prev, ...demoPrefill }))
          setClinicName('Demo Wellness Clinic')
        }
        setIsValidating(false)
        setInitialLoadComplete(true)
        return
      }

      try {
        // First check if there's existing draft data
        const savedData = sessionStorage.getItem(`weightloss-intake-${token}`)
        const savedStep = sessionStorage.getItem(`weightloss-intake-step-${token}`)
        const hasDraft = savedData && savedData !== JSON.stringify(initialFormData)

        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
        const response = await fetch(`${apiUrl}/intakes/validate/${token}`)
        const data = await response.json()

        if (!data.success) {
          setIsCancelled(true)
          setCancelledMessage(data.message || 'This intake link is no longer valid.')
          sessionStorage.removeItem(`weightloss-intake-${token}`)
        } else {
          // Fetch prefill data using the new prefill endpoint
          try {
            const prefillResult = await getIntakePrefillData(token)
            if (prefillResult.success && prefillResult.data) {
              const mappedPrefill = mapPrefillToWeightLossIntake(prefillResult.data)
              setPrefillData(mappedPrefill)
              
              if (hasDraft) {
                // Merge prefill with draft (draft takes priority)
                const draftData = JSON.parse(savedData)
                setFormData({ ...initialFormData, ...mappedPrefill, ...draftData })
                // Restore step if saved
                if (savedStep) {
                  const step = parseInt(savedStep, 10)
                  if (step > 0 && step <= totalSteps) {
                    setCurrentStep(step)
                  }
                }
              } else {
                // No draft - use prefill as base
                setFormData({ ...initialFormData, ...mappedPrefill })
              }
            } else if (hasDraft) {
              // No prefill but has draft - load draft
              setFormData(JSON.parse(savedData))
              if (savedStep) {
                const step = parseInt(savedStep, 10)
                if (step > 0 && step <= totalSteps) {
                  setCurrentStep(step)
                }
              }
            }
          } catch (error) {
            console.error('Error fetching prefill data:', error)
            // Load draft if available
            if (hasDraft) {
              setFormData(JSON.parse(savedData))
              if (savedStep) {
                const step = parseInt(savedStep, 10)
                if (step > 0 && step <= totalSteps) {
                  setCurrentStep(step)
                }
              }
            }
          }
          
          // Get clinic info
          const clinic = data.data?.clinic || data.clinic
          const resolvedClinicName = data.data?.clinicName || clinic?.name || ''
          if (resolvedClinicName) {
            setClinicName(resolvedClinicName)
          }
          if (
            clinic &&
            typeof clinic.mdi_enabled === 'boolean' &&
            typeof clinic.has_prescribing_providers === 'boolean'
          ) {
            // Check if clinic has its own providers (skip referral step if so)
            setShowReferralStep(clinic.mdi_enabled && !clinic.has_prescribing_providers)
          }
        }
      } catch (error) {
        console.error('Token validation error:', error)
      } finally {
        setIsValidating(false)
        setInitialLoadComplete(true)
      }
    }

    validateAndLoadData()
  }, [token, totalSteps])

  // Check if already submitted
  useEffect(() => {
    const submittedStatus = sessionStorage.getItem(`weightloss-intake-submitted-${token}`)
    if (submittedStatus === 'true') {
      setIsSubmitted(true)
    }
  }, [token])

  // Note: Draft loading is now handled in the validation effect above
  // to ensure proper merging with prefill data

  // Save form data on change (only after initial load to avoid race condition)
  useEffect(() => {
    if (initialLoadComplete) {
      sessionStorage.setItem(`weightloss-intake-${token}`, JSON.stringify(formData))
      sessionStorage.setItem(`weightloss-intake-step-${token}`, String(currentStep))
    }
  }, [formData, token, currentStep, initialLoadComplete])

  // Show save notification periodically
  useEffect(() => {
    if (currentStep > 1) {
      toast({
        title: '✓ Progress saved',
        description: 'You can close this and return anytime using the same link.',
        duration: 3000,
      })
    }
  }, [currentStep])

  const updateFormData = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Validation for each step
  const canProceed = () => {
    switch (currentStep) {
      case 1: // MDI Readiness
        return (
          formData.firstName &&
          formData.lastName &&
          formData.dateOfBirth &&
          formData.gender &&
          formData.email &&
          formData.phone &&
          isValidPhone(formData.phone) &&
          formData.addressLine1 &&
          formData.city &&
          formData.state &&
          formData.postalCode &&
          formData.telehealthLocationState &&
          formData.governmentIdKey
        )
      case 2: // Goals
        // Validate height: feet must be 2-10, inches must be 0-11
        const isHeightFeetValid = formData.heightFeet !== null && formData.heightFeet >= 2 && formData.heightFeet <= 10
        const isHeightInchesValid = formData.heightInches === null || (formData.heightInches >= 0 && formData.heightInches <= 11)
        return (
          formData.currentWeight &&
          formData.goalWeight &&
          formData.heightFeet &&
          isHeightFeetValid &&
          isHeightInchesValid &&
          formData.weightLossGoal &&
          formData.weightDistribution &&
          formData.previousAttempts
        )
      case 3: // Current Medications
        return formData.currentGlp1Type !== ''
      case 4: // Medical Screening
        return (
          formData.metabolicConditions.length > 0 &&
          formData.contraindications.length > 0 &&
          formData.familyHistory.length > 0 &&
          formData.kidneyConditions.length > 0
        )
      case 5: // Safety Assessment
        // Check for critical stop flags
        if (formData.pregnancyStatus === 'pregnant' || formData.pregnancyStatus === 'breastfeeding') {
          return false // Cannot proceed
        }
        return (
          formData.glp1Consent &&
          (formData.gender === 'male' || formData.pregnancyStatus) &&
          formData.lastProviderVisit
        )
      case 6: // Lifestyle
        return (
          formData.allergies &&
          formData.currentMedications &&
          (formData.bloodPressureUnknown || (formData.bloodPressureSystolic && formData.bloodPressureDiastolic))
        )
      case 7: // Referral (if shown) or Address (if not)
        if (showReferralStep) {
          return formData.referralReason !== ''
        }
        // Fall through to Address validation (when referral is hidden, step 7 is address)
        {
          const billingOk = Boolean(
            formData.billingAddressLine1?.trim() &&
            formData.billingCity?.trim() &&
            formData.billingState?.trim() &&
            formData.billingPostalCode?.trim() &&
            /^\d{5}(-\d{4})?$/.test(formData.billingPostalCode.trim())
          )
          if (!billingOk) return false
          if (!formData.billingSameAsShipping) {
            return Boolean(
              formData.shippingAddressLine1?.trim() &&
              formData.shippingCity?.trim() &&
              formData.shippingState?.trim() &&
              formData.shippingPostalCode?.trim() &&
              /^\d{5}(-\d{4})?$/.test(formData.shippingPostalCode.trim())
            )
          }
          return true
        }
      case 8: // Address (when referral step is shown) or Review (when not)
        if (showReferralStep) {
          // This is the address step
          const billingOk = Boolean(
            formData.billingAddressLine1?.trim() &&
            formData.billingCity?.trim() &&
            formData.billingState?.trim() &&
            formData.billingPostalCode?.trim() &&
            /^\d{5}(-\d{4})?$/.test(formData.billingPostalCode.trim())
          )
          if (!billingOk) return false
          if (!formData.billingSameAsShipping) {
            return Boolean(
              formData.shippingAddressLine1?.trim() &&
              formData.shippingCity?.trim() &&
              formData.shippingState?.trim() &&
              formData.shippingPostalCode?.trim() &&
              /^\d{5}(-\d{4})?$/.test(formData.shippingPostalCode.trim())
            )
          }
          return true
        }
        // Review step (no referral)
        return (
          formData.consentShareWithMDI &&
          formData.consentAsyncWorkflow &&
          formData.consentTelemedicine &&
          formData.consentAccurate &&
          formData.consentTerms
        )
      case 9: // Review (when referral step is shown)
        return (
          formData.consentShareWithMDI &&
          formData.consentAsyncWorkflow &&
          formData.consentTelemedicine &&
          formData.consentAccurate &&
          formData.consentTerms
        )
      default:
        return true
    }
  }

  const handleNext = () => {
    if (currentStep < totalSteps && canProceed()) {
      setCurrentStep(currentStep + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleSubmit = async () => {
    if (!canProceed()) return

    setIsSubmitting(true)
    setSubmissionError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'

      // Re-validate before submission
      if (!token?.startsWith('demo')) {
        const validateResponse = await fetch(`${apiUrl}/intakes/validate/${token}`)
        const validateData = await validateResponse.json()

        if (!validateData.success) {
          setIsCancelled(true)
          setCancelledMessage(validateData.message || 'This intake link is no longer valid.')
          sessionStorage.removeItem(`weightloss-intake-${token}`)
          setIsSubmitting(false)
          return
        }
      }

      const response = await fetch(`${apiUrl}/intakes/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: formData,
          therapyCategory: 'glp1',
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setIsSubmitted(true)
        sessionStorage.setItem(`weightloss-intake-submitted-${token}`, 'true')
        sessionStorage.removeItem(`weightloss-intake-${token}`)
        sessionStorage.removeItem(`weightloss-intake-step-${token}`)
      } else {
        if (data.message?.includes('cancelled') || data.message?.includes('expired')) {
          setIsCancelled(true)
          setCancelledMessage(data.message)
          sessionStorage.removeItem(`weightloss-intake-${token}`)
        } else {
          setSubmissionError(data.message || 'Failed to submit intake. Please try again.')
        }
      }
    } catch (error) {
      console.error('Submission error:', error)
      setSubmissionError('Network error. Please check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check for risk flags
  const hasRiskFlags = 
    formData.contraindications.some(c => c !== 'none') ||
    formData.familyHistory.some(f => f !== 'none')

  // Render loading state
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1816]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-white/60">Loading your intake form...</p>
        </div>
      </div>
    )
  }

  // Render cancelled state
  if (isCancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1816]">
        <div className="max-w-md text-center p-8 bg-[#1a2332] rounded-2xl shadow-lg border border-white/10">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-500/10">
            <span className="text-3xl">🚫</span>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-white font-montserrat">
            Intake Cancelled
          </h1>
          <p className="mb-4 text-white/60">
            {cancelledMessage || 'This intake form is no longer available.'}
          </p>
          <p className="text-sm text-white/40">
            Please contact your healthcare provider if you need to complete a new intake.
          </p>
        </div>
      </div>
    )
  }

  // Render submitted state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1816]">
        <div className="max-w-md text-center p-8 bg-[#1a2332] rounded-2xl shadow-lg border border-white/10">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-[#4A8A9A]/10">
            <CheckCircle2 className="h-10 w-10 text-[#4A8A9A]" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white font-montserrat">
            Thank You!
          </h1>
          <p className="text-lg mb-6 text-white/60">
            Your weight loss assessment has been submitted successfully.
          </p>
          <div className="p-6 rounded-xl mb-8 bg-[#4A8A9A]/10 border border-[#4A8A9A]/30">
            <h3 className="font-semibold mb-3 text-[#4A8A9A]">Next Steps</h3>
            <ul className="space-y-2 text-sm text-left text-white/90">
              <li>• A licensed clinician will review your assessment</li>
              <li>• You'll receive an email with next steps within 24 hours</li>
              <li>• If approved, your prescription will be sent to the pharmacy</li>
              <li>• Our team may contact you if additional information is needed</li>
            </ul>
          </div>
          <p className="text-sm text-white/60">
            You can close this window now.
          </p>
        </div>
      </div>
    )
  }

  // Get the appropriate step labels based on whether referral step is shown
  const stepLabels = showReferralStep 
    ? STEP_TITLES 
    : STEP_TITLES.filter((_, i) => i !== 6) // Remove 'Referral'

  // Render step content — uses currentStep directly (matches canProceed logic)
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <MDIReadinessStep
            formData={formData}
            updateFormData={updateFormData}
            prefillData={prefillData}
            sessionToken={token}
          />
        )
      case 2:
        return (
          <GoalsStep
            formData={formData}
            updateFormData={updateFormData}
          />
        )
      case 3:
        return (
          <CurrentMedicationsStep
            formData={formData}
            updateFormData={updateFormData}
            sessionToken={token}
          />
        )
      case 4:
        return (
          <MedicalScreeningStep
            formData={formData}
            updateFormData={updateFormData}
          />
        )
      case 5:
        return (
          <SafetyAssessmentStep
            formData={formData}
            updateFormData={updateFormData}
            sessionToken={token}
            gender={formData.gender}
          />
        )
      case 6:
        return (
          <LifestyleStep
            formData={formData}
            updateFormData={updateFormData}
          />
        )
      case 7:
        // When referral step is shown, step 7 = Referral; otherwise step 7 = Address
        if (showReferralStep) {
          return (
            <ReferralContextStep
              formData={formData}
              updateFormData={updateFormData}
              clinicName={clinicName}
              sessionToken={token}
            />
          )
        }
        return (
          <ShippingBillingAddressStep
            shippingAddress={{
              addressLine1: formData.shippingAddressLine1,
              addressLine2: formData.shippingAddressLine2,
              city: formData.shippingCity,
              state: formData.shippingState,
              postalCode: formData.shippingPostalCode,
            }}
            billingAddress={{
              addressLine1: formData.billingAddressLine1,
              addressLine2: formData.billingAddressLine2,
              city: formData.billingCity,
              state: formData.billingState,
              postalCode: formData.billingPostalCode,
            }}
            billingSameAsShipping={formData.billingSameAsShipping}
            onShippingChange={(field, value) => {
              const fieldMap: Record<string, string> = {
                addressLine1: 'shippingAddressLine1',
                addressLine2: 'shippingAddressLine2',
                city: 'shippingCity',
                state: 'shippingState',
                postalCode: 'shippingPostalCode',
              }
              updateFormData(fieldMap[field], value)
            }}
            onBillingChange={(field, value) => {
              const fieldMap: Record<string, string> = {
                addressLine1: 'billingAddressLine1',
                addressLine2: 'billingAddressLine2',
                city: 'billingCity',
                state: 'billingState',
                postalCode: 'billingPostalCode',
              }
              updateFormData(fieldMap[field], value)
            }}
            onBillingSameToggle={(value) => updateFormData('billingSameAsShipping', value)}
            variant="dark"
          />
        )
      case 8:
        // When referral step is shown, step 8 = Address; otherwise step 8 = Review
        if (showReferralStep) {
          return (
            <ShippingBillingAddressStep
              shippingAddress={{
                addressLine1: formData.shippingAddressLine1,
                addressLine2: formData.shippingAddressLine2,
                city: formData.shippingCity,
                state: formData.shippingState,
                postalCode: formData.shippingPostalCode,
              }}
              billingAddress={{
                addressLine1: formData.billingAddressLine1,
                addressLine2: formData.billingAddressLine2,
                city: formData.billingCity,
                state: formData.billingState,
                postalCode: formData.billingPostalCode,
              }}
              billingSameAsShipping={formData.billingSameAsShipping}
              onShippingChange={(field, value) => {
                const fieldMap: Record<string, string> = {
                  addressLine1: 'shippingAddressLine1',
                  addressLine2: 'shippingAddressLine2',
                  city: 'shippingCity',
                  state: 'shippingState',
                  postalCode: 'shippingPostalCode',
                }
                updateFormData(fieldMap[field], value)
              }}
              onBillingChange={(field, value) => {
                const fieldMap: Record<string, string> = {
                  addressLine1: 'billingAddressLine1',
                  addressLine2: 'billingAddressLine2',
                  city: 'billingCity',
                  state: 'billingState',
                  postalCode: 'billingPostalCode',
                }
                updateFormData(fieldMap[field], value)
              }}
              onBillingSameToggle={(value) => updateFormData('billingSameAsShipping', value)}
              variant="dark"
            />
          )
        }
        return (
          <ReviewStep
            formData={formData}
            updateFormData={updateFormData}
            hasRiskFlags={hasRiskFlags}
          />
        )
      case 9:
        return (
          <ReviewStep
            formData={formData}
            updateFormData={updateFormData}
            hasRiskFlags={hasRiskFlags}
          />
        )
      default:
        return null
    }
  }

  const isLastStep = currentStep === totalSteps

  return (
    <div 
      ref={containerRef}
      className="min-h-screen intake-dark-form"
      style={{ 
        backgroundColor: '#000',
        backgroundImage: 'url(/img/footer-bg.png)',
        backgroundSize: '100% auto',
        backgroundPosition: 'bottom',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Nimbus-style Container */}
      <div className="w-full relative max-w-[500px] mx-auto px-5 md:px-0">
        <div className="flex h-full flex-col md:pt-[50px] pt-[30px]">
          {/* Brand Logo */}
          <div className="brand pb-[30px]">
            <img
              src="/novamed_logo_white.svg"
              alt="Nimbus Providers"
              className="w-[140px] md:w-[180px] h-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <p className="text-white/80 text-sm mt-3 font-medium">
              {clinicName || 'Nimbus Providers'}
            </p>
          </div>

          {/* Progress Bar with Back Button */}
          <div className="quiz-progress py-4 pb-[30px]">
            <ProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
              stepLabels={stepLabels}
              onBack={handleBack}
              showBackButton={currentStep > 1}
            />
          </div>

          {/* Step Content with Animation */}
          <div className="flex flex-col flex-1 step-slide-next">
            {renderStep()}

            {/* Continue / Submit Button - Nimbus Style */}
            <div className="flex gap-3 text-center mt-[40px] pb-5 md:pb-[50px] sticky w-full bottom-0 z-[99]">
              {isLastStep ? (
                <button
                  onClick={handleSubmit}
                  disabled={!canProceed() || isSubmitting}
                  className="w-full min-h-[56px] font-semibold text-base leading-[100%] tracking-tight cursor-pointer text-center py-[20px] rounded-[60px] bg-white text-[#232324] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Assessment
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="w-full min-h-[56px] font-semibold text-base leading-[100%] tracking-tight cursor-pointer text-center py-[20px] rounded-[60px] bg-white text-[#232324] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              )}
            </div>
          </div>

          {/* Error Message */}
          {submissionError && (
            <div className="mb-8 p-4 rounded-[14px] bg-red-500/10 border border-red-500/30 text-center">
              <p className="text-red-400">{submissionError}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Export default for simple import
export default WeightLossIntakeLanding
